import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

const ProfileEmbeddingsManager = () => {
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState<any>(null);
  const { toast } = useToast();

  const handleBackfillEmbeddings = async () => {
    setIsBackfilling(true);
    setBackfillResult(null);

    try {
      console.log('Starting profile embeddings backfill...');
      
      const { data, error } = await supabase.functions.invoke('backfill-profile-embeddings');
      
      if (error) {
        console.error('Backfill error:', error);
        toast({
          title: "Backfill Failed",
          description: error.message || "Failed to backfill embeddings",
          variant: "destructive",
        });
        return;
      }

      console.log('Backfill result:', data);
      setBackfillResult(data);
      
      toast({
        title: "Backfill Completed",
        description: `Processed: ${data.processed}, Errors: ${data.errors}`,
      });
    } catch (error) {
      console.error('Backfill error:', error);
      toast({
        title: "Backfill Failed",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsBackfilling(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Profile Embeddings Manager</CardTitle>
          <CardDescription>
            Generate embeddings for user profiles to enable semantic search functionality.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Button 
              onClick={handleBackfillEmbeddings}
              disabled={isBackfilling}
              className="flex items-center gap-2"
            >
              {isBackfilling && <Loader2 className="h-4 w-4 animate-spin" />}
              {isBackfilling ? 'Backfilling...' : 'Backfill Profile Embeddings'}
            </Button>
          </div>

          {backfillResult && (
            <div className="p-4 bg-muted rounded-lg">
              <h4 className="font-semibold mb-2">Backfill Results:</h4>
              <div className="space-y-1 text-sm">
                <p>Total profiles: {backfillResult.total}</p>
                <p>Successfully processed: {backfillResult.processed}</p>
                <p>Errors: {backfillResult.errors}</p>
              </div>
            </div>
          )}

          <div className="text-sm text-muted-foreground">
            <p>This process will:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Find profiles with bio content</li>
              <li>Generate text embeddings for semantic search</li>
              <li>Store embeddings in the profile_vectors table</li>
              <li>Enable advanced profile discovery features</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileEmbeddingsManager;